import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { WELCOME_TOUR_SLIDES, GUIDE_STORAGE_KEY } from '../data/platformTutorial';

  const PUBLIC_PATHS = ['/', '/login', '/auth', '/reset-password', '/dashboard', '/generate'];
const NON_BLOCKING_PATHS = [/^\/edit\//, /^\/sites\/[^/]+\/operate/, /^\/marketing\//, /^\/ai-tools\//];

export default function WelcomeTourModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    if (localStorage.getItem(GUIDE_STORAGE_KEY)) return;
    const path = location.pathname;
    if (PUBLIC_PATHS.includes(path) || NON_BLOCKING_PATHS.some((pattern) => pattern.test(path)) || /^\/w\/[^/]+\/auth$/.test(path) || /^\/w\/[^/]+$/.test(path)) return;
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const dismiss = (markSeen = true) => {
    if (markSeen) localStorage.setItem(GUIDE_STORAGE_KEY, '1');
    setVisible(false);
  };

  const current = WELCOME_TOUR_SLIDES[slide];
  const isLast = slide === WELCOME_TOUR_SLIDES.length - 1;

  const next = () => {
    if (isLast) {
      dismiss(true);
      navigate('/platform-guide');
      return;
    }
    setSlide((s) => s + 1);
  };

  const prev = () => setSlide((s) => Math.max(0, s - 1));

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80" data-testid="welcome-tour-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d0d14] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-amber-400" size={18} />
            <span className="text-sm font-semibold text-white">Getting started</span>
          </div>
          <button type="button" onClick={() => dismiss(true)} className="text-zinc-500 hover:text-white" aria-label="Skip tour">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-8 min-h-[200px]">
          <p className="text-xs text-violet-400 mb-2">
            Step {slide + 1} of {WELCOME_TOUR_SLIDES.length}
          </p>
          <h2 className="text-2xl font-bold text-white">{current.title}</h2>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{current.body}</p>
          {current.pathLabel && (
            <button
              type="button"
              onClick={() => { dismiss(true); navigate(current.path); }}
              className="mt-4 text-sm text-violet-300 hover:text-violet-200 underline"
            >
              {current.pathLabel} →
            </button>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-4">
          <div className="flex gap-1">
            {WELCOME_TOUR_SLIDES.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === slide ? 'w-6 bg-violet-400' : 'w-1.5 bg-white/20'}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {slide > 0 && (
              <button
                type="button"
                onClick={prev}
                className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-sm text-zinc-400 hover:text-white"
              >
                <ChevronLeft size={16} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="flex items-center gap-1 rounded-lg bg-violet-500 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-400"
            >
              {current.cta}
              {!isLast && <ChevronRight size={16} />}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => dismiss(true)}
          className="w-full py-2 text-xs text-zinc-600 hover:text-zinc-400 border-t border-white/5"
        >
          Skip tour — I will explore on my own
        </button>
      </div>
    </div>
  );
}
